import {
  AddressFormInputs,
  Button,
  Input,
  InputWrapper,
} from '@vrplatform/components';
import { useMutation, useTeam } from '@vrplatform/data';
import { AlertTriangleIcon } from '@vrplatform/icons';
import { Drawer } from '@vrplatform/components-legacy';
import { showErrorNotification } from '@vrplatform/theme';
import { Box, Group, ScrollArea } from '@mantine/core';
import {
  type Address,
  formatAddress,
  getCountryByIsoCode,
  getStateByIsoCode,
} from '@vrplatform/ui-common';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  DrawerHeader,
  DrawerSectionTitle,
} from '../../../../drawers/_components';

type Props = {
  opened: boolean;
  closeDrawer: () => void;
};

type FormInputs = {
  title: string;
  address: Parameters<typeof formatAddress>[0] | null | undefined;
};

export const AddListingDrawer = ({ closeDrawer, opened }: Props) => {
  const [{ id: teamId }] = useTeam();

  const methods = useForm<FormInputs>();

  const { mutate } = useMutation(
    (
      q,
      args: {
        title: string;
        address: (Address & { country: string; state: string }) | null;
        tenantId: string;
      }
    ) => {
      const line = args.address?.line1.trim();
      const line2 = args.address?.line2?.trim() || '';
      const city = args.address?.city.trim();
      const state = args.address?.state?.trim();
      const postalCode = args.address?.postcode.trim();
      const country = args.address?.country.trim();

      return q.insertListing({
        object: {
          title: args.title,
          tenantId: args.tenantId,
          addressRelation:
            args.address && line && city && postalCode
              ? {
                  data: {
                    line,
                    lineDetails: line2,
                    city,
                    state,
                    postalCode,
                    country,
                    stateCode: args.address.stateCode,
                    countryCode: args.address.countryCode,
                    full: formatAddress({
                      line1: line,
                      line2,
                      city,
                      state,
                      postcode: postalCode,
                      country,
                      stateCode: args.address.stateCode,
                      countryCode: args.address.countryCode,
                    }),
                    tenant_id: args.tenantId,
                  },
                  on_conflict: {
                    constraint: 'address_pkey',
                    update_columns: [
                      'line',
                      'lineDetails',
                      'city',
                      'state',
                      'postalCode',
                      'country',
                      'full',
                    ],
                  },
                }
              : undefined,
        },
      })?.id;
    },
    {
      invalidateQueryKeys: ['listingConnections', 'listings'],
    }
  );

  const submit = async (data: FormInputs) => {
    const hasAddress = !!data.address?.line1?.trim();

    if (
      hasAddress &&
      (!data.address?.stateCode || !data.address?.countryCode)
    ) {
      return showErrorNotification({
        title: 'Validation error',
        message: 'Please select a country and state.',
        icon: <AlertTriangleIcon size={20} />,
      });
    }

    const country =
      (data.address?.countryCode &&
        getCountryByIsoCode(data.address?.countryCode)?.name) ||
      '';
    const state =
      (data.address?.countryCode &&
        data.address?.stateCode &&
        getStateByIsoCode(data.address?.stateCode, data.address?.countryCode)
          ?.name) ||
      '';

    await mutate({
      args: {
        tenantId: teamId,
        title: data.title.trim(),
        address: hasAddress
          ? {
              city: data.address!.city.trim(),
              line1: data.address!.line1.trim(),
              line2: data.address!.line2?.trim(),
              stateCode: data.address!.stateCode!,
              state,
              countryCode: data.address!.countryCode!,
              country,
              postcode: data.address!.postcode.trim(),
            }
          : null,
      },
    })
      .then(closeDrawer)
      .catch((err) => console.error(err));
  };

  return (
    <Drawer
      opened={opened}
      onClose={closeDrawer}
      containerSx={{ paddingInline: 0 }}
    >
      <DrawerHeader title="Add listing" closeDrawer={closeDrawer} />

      <FormProvider {...methods}>
        <Box
          sx={() => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflowY: 'hidden',
            overflowX: 'unset',
          })}
        >
          <ScrollArea
            styles={(theme) => ({
              root: {
                flex: 1,
              },
              viewport: {
                paddingInline: theme.spacing.sm,
              },
            })}
          >
            <Controller
              control={methods.control}
              name="title"
              defaultValue=""
              rules={{
                required: 'Name is required',
                validate: trapSpacesForRequiredFields,
              }}
              render={({ field, fieldState: { error } }) => (
                <InputWrapper
                  label="Name"
                  error={error?.message}
                  required={true}
                >
                  <Input
                    {...field}
                    placeholder="Name"
                    autoComplete="off"
                    error={!!error}
                  />
                </InputWrapper>
              )}
            />

            <DrawerSectionTitle>Address</DrawerSectionTitle>

            <AddressFormInputs />
          </ScrollArea>

          <Box
            sx={(theme) => ({
              paddingBlock: theme.spacing.sm,
              borderTop: `1px solid ${theme.colors.gray[2]}`,
              paddingInline: theme.spacing.sm,
            })}
          >
            <Group>
              <Button
                onClick={() => closeDrawer()}
                disabled={methods.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                sx={{ flexGrow: '1!important' as any }}
                type="submit"
                disabled={!methods.formState.isDirty}
                onClick={methods.handleSubmit(submit)}
                variant="primary"
                loading={methods.formState.isSubmitting}
              >
                Submit
              </Button>
            </Group>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';
